import { Button } from '@packages/ui/shared';
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { FormProvider, useFormContext } from './battery-context';

import { analyticsService, evBatteryService } from '@web/services/singletons';

// existing steps you already have
import StepOneContent from './step-contents/step-one-content';
import StepTwoContent from './step-contents/step-two-content';
import StepFiveContent from './step-contents/step-five-content';

// NEW unified component for steps 3 & 4
import Step34Content from './step-contents/step34-content';
import { UpdateBatteryVin } from '@packages/models/api';
import { useVinSessionState } from '@web/vin-sessions/context/vin-sessions.hooks';
import { useAuthState } from '@packages/contexts/auth';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';

type BinUpdateFormWizardProps = {
    open: boolean;
    closeCallback: (result: any) => void;
    existingBin?: string;
};

const useStyles = createUseStyles({
    header: {
        padding: '16px 24px!important',
    },
    title: {
        fontSize: '21px!important',
        fontWeight: '600!important',
    },
    body: {
        padding: '16px 24px!important',
    },
    bodyDescription: {
        fontSize: '15px!important',
        fontWeight: '400!important',
    },
    footer: {
        padding: '24px!important',
        display: 'flex',
        justifyContent: 'space-between !important',
        alignItems: 'center',
        paddingLeft: '10px',
        paddingRight: '10px',
    },
    formLabel: {
        fontSize: '15px',
    },
    headerSearchRetailer: {
        minWidth: 256,
    },
    buttonWrapper: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
});

const BinUpdateFormWizardWithContext: React.FC<BinUpdateFormWizardProps> = (props: BinUpdateFormWizardProps) => {
    const LAST_STEP_NUMBER = 5;
    const classes = useStyles();
    const { t } = useTranslation();
    const { selectedVin } = useVinSessionState();
    const { account } = useAuthState();
    const [displayModal, setDisplayModal] = useState<boolean>(false);
    const [currentStep, setCurrentStep] = useState(1);
    const [displayQrCodeScanner, setDisplayQrCodeScanner] = useState<boolean>(false);
    const { data, validateForm, resetForm, setOldBinFromExisting } = useFormContext();

    // Set old bin from existing bin if provided - only run once when component mounts
    useEffect(() => {
        if (props.existingBin) {
            setOldBinFromExisting(props.existingBin);
        }
    }, [props.existingBin]); // Remove setOldBinFromExisting from deps since it's stable

    const hasExistingOldBin = () => {
        return props.existingBin && props.existingBin.length > 0;
    };

    const enabledContinueButton = () => {
        if (currentStep === 2) {
            return (
                data?.repairOrderNumber?.length &&
                data?.repairOrderNumber?.length >= 4 &&
                data?.repairOrderNumber?.length <= 10 &&
                data.selectedRetailer?.length > 0
            );
        }

        if (currentStep === 3) return data?.newBinQrCode?.length > 0;
        if (currentStep === 4) return data?.oldBinQrCode?.length > 0;

        if (currentStep !== LAST_STEP_NUMBER) return true;

        // Step 5: require BOTH part numbers to be present (non-empty)
        const newPart = data?.parsedNewBinData?.partNumber?.trim?.() ?? '';
        const oldPart = data?.parsedOldBinData?.partNumber?.trim?.() ?? '';
        const bothHavePartNumbers = newPart.length > 0 && oldPart.length > 0;

        return bothHavePartNumbers;
    };

    useEffect(() => {
        setDisplayModal(props.open);
    }, [props.open]);

    const onHide = () => {
        if (displayQrCodeScanner) {
            setDisplayQrCodeScanner(false);
        } else {
            setDisplayModal(false);
            setDisplayQrCodeScanner(false);
            setCurrentStep(1);
            resetForm();
            props.closeCallback('cancel');
        }
    };

    const onContinue = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        if (!validateForm(currentStep)) return;
        if (currentStep < LAST_STEP_NUMBER) {
            // Skip step 4 if old bin already exists
            if (currentStep === 3 && hasExistingOldBin()) {
                setCurrentStep(5); // Jump directly to step 5
            } else {
                setCurrentStep(currentStep + 1);
            }
        } else {
            const formatDate = (date: Date) => {
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const day = date.getDate().toString().padStart(2, '0');
                const year = date.getFullYear();
                return `${year}-${month}-${day}`;
            };
            const productionDate = data.parsedNewBinData?.productionDate;
            const yearYY = productionDate?.substring(0, 2); // "25"
            const month = productionDate?.substring(2, 4); // "02"
            const day = productionDate?.substring(4, 6); // "19"
            const productionDateYYYYMMDD = `20${yearYY}-${month}-${day}`;
            const updatebatteryVinBody: UpdateBatteryVin = {
                vin: selectedVin?.vinRecord.vin || '',
                oldBin: data.oldBinQrCode,
                newBin: data.newBinQrCode,
                dateOfScan: formatDate(new Date()),
                retailerNumber: data.selectedRetailer[0].retailerNumber,
                technicianId: account?.soaUsername || '',
                repairOrderNumber: data.repairOrderNumber || '',
                partNumber: data.parsedNewBinData?.partNumber || '',
                manufacturingDate: productionDateYYYYMMDD,
            };
            analyticsService.logEvent(ANALYTICS_EVENTS.WEB_SUBMIT_ATTEMPT, updatebatteryVinBody);
            evBatteryService
                .postEvBatteryVin({ updatebatteryVinBody })
                .then((result) => {
                    analyticsService.logEvent(ANALYTICS_EVENTS.WEB_SUBMIT_SUCCESS, updatebatteryVinBody);
                    setDisplayModal(false);
                    setDisplayQrCodeScanner(false);
                    setCurrentStep(1);
                    resetForm();
                    props.closeCallback(result);
                })
                .catch((err) => {
                    analyticsService.logEvent(ANALYTICS_EVENTS.WEB_SUBMIT_FAIL, updatebatteryVinBody);
                    setDisplayModal(false);
                    setDisplayQrCodeScanner(false);
                    setCurrentStep(1);
                    resetForm();
                    props.closeCallback(new Error(err));
                });
        }
    };

    const goBack = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
        e.preventDefault();
        e.stopPropagation();
        if (currentStep > 1) {
            // Skip step 4 when going back if old bin already exists
            if (currentStep === 5 && hasExistingOldBin()) {
                setCurrentStep(3); // Jump directly back to step 3
            } else {
                setCurrentStep(currentStep - 1);
            }
            if (displayQrCodeScanner) {
                setDisplayQrCodeScanner(false);
            }
        }
    };

    return (
        <Modal show={displayModal} centered={false} onHide={onHide} size="lg">
            <Modal.Header>
                <Modal.Title>EV - Battery update</Modal.Title>
            </Modal.Header>

            {/* Step 1 & 2 remain as-is */}
            <StepOneContent currentStep={currentStep} displayQrCodeScanner={displayQrCodeScanner} />
            <StepTwoContent currentStep={currentStep} displayQrCodeScanner={displayQrCodeScanner} />

            {/* Unified Step 3: New Bin */}
            <Step34Content
                currentStep={currentStep}
                targetStep={3}
                displayQrCodeScanner={displayQrCodeScanner}
                setDisplayQrCodeScanner={setDisplayQrCodeScanner}
                storageKey="newBinQrCode"
                headingLabel="Upload or scan the new QR code on the label of the new battery pack you installed"
                resultLabel="New Bin"
            />

            {/* Unified Step 4: Old Bin - Only show if no existing bin */}
            {!hasExistingOldBin() && (
                <Step34Content
                    currentStep={currentStep}
                    targetStep={4}
                    displayQrCodeScanner={displayQrCodeScanner}
                    setDisplayQrCodeScanner={setDisplayQrCodeScanner}
                    storageKey="oldBinQrCode"
                    headingLabel="Upload or scan the old QR label of the replaced battery pack."
                    resultLabel="Old Bin"
                />
            )}

            {/* Step 5 remains as-is (table renders) */}
            <StepFiveContent currentStep={currentStep} displayQrCodeScanner={displayQrCodeScanner} />

            <Modal.Footer className={classes.footer}>
                {currentStep > 1 ? (
                    <Button variant="ghost-blue" onPress={goBack}>
                        Back
                    </Button>
                ) : (
                    <div></div>
                )}

                <div className={classes.buttonWrapper}>
                    <div className="mr-2">
                        <Button variant="ghost-blue" onPress={onHide}>
                            Cancel
                        </Button>
                    </div>

                    <Button
                        variant="primary"
                        onPress={onContinue}
                        disabled={!enabledContinueButton()}
                        style={{ display: displayQrCodeScanner ? 'none' : 'flex' }}
                    >
                        {currentStep === LAST_STEP_NUMBER ? 'Confirm And Update' : 'Continue'}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

const BinUpdateFormWizard: React.FC<BinUpdateFormWizardProps> = (props: BinUpdateFormWizardProps) => (
    <FormProvider>
        <BinUpdateFormWizardWithContext {...props} />
    </FormProvider>
);

export default BinUpdateFormWizard;
